import { comparePassengerNames } from "../utils/nameSorting";
// src/pages/PassengersPage.js

import ImmediateTimePicker from "../components/ImmediateTimePicker";
import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Checkbox,
  Select,
  Tag,
  message,
  Switch,
  Space,
  Progress,
  Alert,
  Card,
  Upload,
  Collapse,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "../api/axios";
import { notesColumnFilter } from "../components/notesColumnFilter";

const WEEKDAY_ORDER = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"];

const WEEKDAY_DE = {
  MONDAY: "Montag",
  TUESDAY: "Dienstag",
  WEDNESDAY: "Mittwoch",
  THURSDAY: "Donnerstag",
  FRIDAY: "Freitag",
};

const COLORS = {
  primary: "#3FB6C2",
  primaryDark: "#1F4E57",
  primaryLight: "#E8F7F9",
  border: "#DCE7F3",
  text: "#1F2937",
  muted: "#6B7280",
};

const GEOCODE_BULK_ENDPOINT = "/geocoding/validate/bulk";
const clearRouteCache = () => {
  // Der letzte gültige Fahrplan bleibt als Ergebnis/Download erhalten.
  localStorage.removeItem("routing.runState");
};

const emptyOverrideRows = () => WEEKDAY_ORDER.map((weekday) => ({
  weekday,
  outboundPickupStart: "", outboundPickupEnd: "",
  outboundArrivalStart: "", outboundArrivalEnd: "",
  returnPickupStart: "", returnPickupEnd: "",
  returnArrivalStart: "", returnArrivalEnd: "",
}));

const mergeOverrideRows = (values = []) => emptyOverrideRows().map((row) => ({
  ...row, ...(values.find((value) => value.weekday === row.weekday) || {}),
}));

const PassengersPage = () => {
  const [passengers, setPassengers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPassenger, setEditingPassenger] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);

  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);

  const [form] = Form.useForm();
  const [facilityForm] = Form.useForm();
  const [facilityModalOpen, setFacilityModalOpen] = useState(false);
  const [timeOverrides, setTimeOverrides] = useState([]);

  const [bulkChecking, setBulkChecking] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [bulkResultOpen, setBulkResultOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState({
    okCount: 0,
    failCount: 0,
    total: 0,
    results: [],
  });

  const [invalidPassengerIds, setInvalidPassengerIds] = useState(
    () => new Set()
  );

  const passengerCount = limits?.usedPassengers ?? passengers.length;
  const passengerLimit = limits?.effectivePassengerLimit ?? null;

  const limitReached =
    limits?.passengerLimitReached ??
    (passengerLimit !== null && passengerCount >= passengerLimit);
  const limitExceeded =
    passengerLimit !== null && passengerCount > passengerLimit;

  const limitText =
    passengerLimit !== null ? passengerLimit : "Limit nicht geladen";

  const fetchPassengers = async () => {
    setLoading(true);

    try {
      const res = await axios.get("/passengers");
      setPassengers(res.data || []);
    } catch (err) {
      message.error("Fehler beim Laden der Passagiere.");
    } finally {
      setLoading(false);
    }
  };

  const fetchLimits = async () => {
    try {
      const res = await axios.get("/users/me/limits");
      setLimits(res.data);
    } catch {
      setLimits(null);
    }
  };

  const fetchFacilities = async () => {
    try {
      const res = await axios.get("/facilities");
      setFacilities(res.data || []);
    } catch { setFacilities([]); }
  };

  const refreshData = async () => {
    await Promise.all([fetchPassengers(), fetchLimits(), fetchFacilities()]);
  };

  useEffect(() => {
    refreshData();
    // eslint-disable-next-line
  }, []);

  const handleSave = async (values) => {
    const payload = normalizePassengerPayload({
      ...(editingPassenger || {}),
      ...values,
      timeOverrides,
    });

    try {
      if (editingPassenger) {
        await axios.put(`/passengers/${editingPassenger.id}`, payload);
        message.success("Passagier aktualisiert.");
      } else {
        await axios.post("/passengers", payload);
        message.success("Passagier erstellt.");
      }

      clearRouteCache();

      setModalOpen(false);
      setEditingPassenger(null);
      form.resetFields();
      refreshData();
    } catch (err) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Speichern fehlgeschlagen.";

      message.error(backendMessage);
    }
  };

  const handleToggleActive = async (passenger) => {
    const updatedPassenger = { ...passenger, aktiv: !passenger.aktiv };

    try {
      await axios.put(`/passengers/${passenger.id}`, updatedPassenger);
      clearRouteCache();

      setPassengers((prev) =>
        prev.map((p) =>
          p.id === passenger.id ? { ...p, aktiv: !p.aktiv } : p
        )
      );

      message.success("Status geändert.");
    } catch {
      message.error("Konnte Status nicht ändern.");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/passengers/${id}`);
      clearRouteCache();
      message.success("Passagier gelöscht.");
    } catch {
      message.error("Löschen fehlgeschlagen.");
    } finally {
      refreshData();
    }
  };

  const handleDeleteAll = async () => {
    setDeleteAllLoading(true);
    try {
      await axios.delete("/passengers");
      clearRouteCache();
      message.success("Alle Passagiere wurden gelöscht.");
      setInvalidPassengerIds(new Set());
      setImportResult(null);
      setDeleteAllOpen(false);
      await refreshData();
    } catch {
      message.error("Alle Passagiere konnten nicht gelöscht werden.");
    } finally {
      setDeleteAllLoading(false);
    }
  };

  const handlePassengerImport = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("file", file);

    setImporting(true);
    setImportResult(null);

    try {
      const res = await axios.post("/passengers/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      clearRouteCache();

      const createdCount = res.data?.createdCount ?? 0;
      const skippedCount = res.data?.skippedCount ?? 0;
      const rowErrors = res.data?.rowErrors || [];

      if (createdCount > 0) {
        message.success(`${createdCount} Passagiere importiert.`);
        setImportResult({
          type: "success",
          message: `${createdCount} Passagiere importiert.`,
        });
      } else {
        setImportResult({
          type: "warning",
          message:
            "Die Datei wurde gelesen, aber es wurden keine Passagiere importiert.",
        });
      }

      if (skippedCount > 0 || rowErrors.length > 0) {
        setImportResult({
          type: createdCount > 0 ? "warning" : "error",
          message: `${createdCount} Passagiere importiert. ${Math.max(
            skippedCount,
            rowErrors.length
          )} Zeilen wurden übersprungen oder konnten nicht gespeichert werden.`,
          details: rowErrors,
        });

        Modal.warning({
          title: "Import mit Hinweisen",
          width: 720,
          content: (
            <div>
              <p>
                {createdCount} Passagiere wurden importiert,{" "}
                {Math.max(skippedCount, rowErrors.length)} Zeilen wurden
                übersprungen oder konnten nicht gespeichert werden.
              </p>
              {rowErrors.length > 0 && (
                <ul style={{ paddingLeft: 18, maxHeight: 260, overflowY: "auto" }}>
                  {rowErrors.slice(0, 30).map((error, index) => (
                    <li key={`${error}-${index}`}>{error}</li>
                  ))}
                  {rowErrors.length > 30 && (
                    <li>Weitere {rowErrors.length - 30} Hinweise ausgeblendet.</li>
                  )}
                </ul>
              )}
            </div>
          ),
        });
      }

      await refreshData();
      onSuccess?.(res.data);
    } catch (err) {
      const data = err?.response?.data || {};
      const backendMessage =
        data.message || data.error || "Import fehlgeschlagen.";

      message.error(backendMessage);
      setImportResult({
        type: "error",
        message: backendMessage,
        parsedCount: data.parsedCount,
        freeSlots: data.freeSlots,
        details: data.rowErrors || [],
      });
      Modal.error({
        title: "Import nicht möglich",
        width: 680,
        content: (
          <div>
            <p>{backendMessage}</p>
            {data.parsedCount != null && data.freeSlots != null && (
              <p>
                Gefundene Passagiere: {data.parsedCount}
                <br />
                Freie Plätze im aktuellen Tarif: {data.freeSlots}
              </p>
            )}
          </div>
        ),
      });
      onError?.(err);
    } finally {
      setImporting(false);
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handlePassengerExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get("/passengers/export", {
        responseType: "blob",
      });
      downloadBlob(res.data, "passagiere.xlsx");
      message.success("Passagierliste wurde heruntergeladen.");
    } catch {
      message.error("Passagierliste konnte nicht heruntergeladen werden.");
    } finally {
      setExporting(false);
    }
  };

  const openEdit = (record) => {
    setEditingPassenger(record);
    setModalOpen(true);
    setTimeOverrides(mergeOverrideRows(record.timeOverrides));

    setTimeout(() => {
      form.setFieldsValue({
        ...record,
        wohnortAdresse: record.wohnortAdresse || record.wohnort?.name || "",
        arbeitsortAdresse:
          record.arbeitsortAdresse || record.arbeitsort?.name || "",
        facilityId: record.facilityId || undefined,
        maxRideMinutes: record.maxRideMinutes ?? null,
        notizen: record.notizen || "",
        umsteigezeitMinuten:
          record.umsteigezeitMinuten ??
          (record.einstiegszeitMinuten ?? 0) + (record.ausstiegszeitMinuten ?? 0),
        unvertraeglichePassengerIds: record.unvertraeglichePassengerIds || [],
        herausforderndesVerhalten: record.herausforderndesVerhalten || false,
        hinfahrtVerfuegbarkeit: record.hinfahrtVerfuegbarkeit || [],
        rueckfahrtVerfuegbarkeit: record.rueckfahrtVerfuegbarkeit || [],
      });
    });
  };

  const openCreate = () => {
    if (limitReached) {
      message.warning(
        `Das Fahrgast-Limit ist erreicht: ${passengerCount} von ${passengerLimit} Fahrgästen.`
      );
      return;
    }

    setEditingPassenger(null);
    setModalOpen(true);
    setTimeOverrides(emptyOverrideRows());

    setTimeout(() => {
      form.resetFields();
      form.setFieldsValue({
        name: "",
        wohnortAdresse: "",
        arbeitsortAdresse: "",
        facilityId: undefined,
        rollstuhl: false,
        eRollstuhl: false,
        beifahrer: false,
        taxi: false,
        aktiv: true,
        notizen: "",
        umsteigezeitMinuten: 0,
        unvertraeglichePassengerIds: [],
        herausforderndesVerhalten: false,
        hinfahrtVerfuegbarkeit: WEEKDAY_ORDER,
        rueckfahrtVerfuegbarkeit: WEEKDAY_ORDER,
      });
    });
  };

  const normalizePassengerPayload = (payload) => {
    const selfId = editingPassenger?.id;
    const incompatibleIds = Array.isArray(payload.unvertraeglichePassengerIds)
      ? payload.unvertraeglichePassengerIds
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0 && id !== selfId)
      : [];

    const selectedFacility = facilities.find((facility) => facility.id === payload.facilityId);
    const cleanedOverrides = (payload.timeOverrides || []).filter((row) =>
      Object.entries(row).some(([key, value]) => key !== "weekday" && !!value)
    );
    return {
      ...payload,
      arbeitsortAdresse: selectedFacility?.address || payload.arbeitsortAdresse,
      timeOverrides: cleanedOverrides,
      rollstuhl: !!payload.rollstuhl,
      maxRideMinutes: payload.maxRideMinutes ?? null,
      umsteigezeitMinuten: Math.max(0, Number(payload.umsteigezeitMinuten) || 0),
      unvertraeglichePassengerIds: Array.from(new Set(incompatibleIds)),
      herausforderndesVerhalten: !!payload.herausforderndesVerhalten,
    };
  };

  const renderSortedWeekdays = (ws) =>
    ws && Array.isArray(ws)
      ? ws
          .slice()
          .sort((a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b))
          .map((w) => (
            <Tag
              key={w}
              style={{
                marginBottom: 4,
                background: COLORS.primaryLight,
                color: COLORS.primaryDark,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 999,
              }}
            >
              {WEEKDAY_DE[w] || w}
            </Tag>
          ))
      : null;

  const passengerSelectOptions = passengers
    .filter((p) => p.id !== editingPassenger?.id)
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "de"))
    .map((p) => ({
      label: p.name || `Fahrgast ${p.id}`,
      value: p.id,
    }));

  const facilityOptions = facilities.map((facility) => ({
    value: facility.id,
    label: `${facility.name} – ${facility.address}`,
  }));

  const handleFacilityChange = (facilityId) => {
    const facility = facilities.find((item) => item.id === facilityId);
    form.setFieldsValue({
      facilityId,
      arbeitsortAdresse: facility?.address || form.getFieldValue("arbeitsortAdresse"),
    });
  };

  const findFacilityForAddress = async () => {
    if (form.getFieldValue("facilityId")) return;
    const address = form.getFieldValue("arbeitsortAdresse");
    if (!address || address.length < 5) return;
    try {
      const res = await axios.get("/facilities/matches", { params: { address } });
      const match = res.data?.[0];
      if (!match) return;
      Modal.confirm({
        title: "Bestehende Einrichtung gefunden",
        content: `Möchtest du „${match.name} – ${match.address}“ übernehmen?`,
        okText: "Übernehmen",
        cancelText: "Adresse separat verwenden",
        onOk: () => handleFacilityChange(match.id),
      });
    } catch (_) {}
  };

  const createFacilityFromPassenger = async (values) => {
    try {
      const res = await axios.post("/facilities", { ...values, dailyWindows: [] });
      clearRouteCache();
      const created = res.data;
      setFacilities((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, "de")));
      form.setFieldsValue({ facilityId: created.id, arbeitsortAdresse: created.address });
      setFacilityModalOpen(false);
      facilityForm.resetFields();
      message.success("Einrichtung angelegt und ausgewählt.");
    } catch (err) { message.error(err?.response?.data?.message || "Einrichtung konnte nicht angelegt werden."); }
  };

  const updateOverride = (weekday, field, value) => setTimeOverrides((prev) => prev.map((row) =>
    row.weekday === weekday ? { ...row, [field]: value } : row));

  const overrideColumns = [
    { title: "Tag", dataIndex: "weekday", fixed: "left", width: 95, render: (v) => WEEKDAY_DE[v] },
    ...[
      ["outboundPickup", "Abholung Zuhause"], ["outboundArrival", "Ankunft Arbeit"],
      ["returnPickup", "Abholung Arbeit"], ["returnArrival", "Ankunft Zuhause"],
    ].map(([prefix, title]) => ({ title, width: 210, render: (_, row) => (
      <Space.Compact>
        <ImmediateTimePicker placeholder="von" value={row[`${prefix}Start`]} onChange={(value) => updateOverride(row.weekday, `${prefix}Start`, value)} />
        <ImmediateTimePicker placeholder="bis" value={row[`${prefix}End`]} onChange={(value) => updateOverride(row.weekday, `${prefix}End`, value)} />
      </Space.Compact>
    ) })),
  ];

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    if (confirm) confirm({ closeDropdown: false });
    setSearchedColumn("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Suche ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />

        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Suchen
        </Button>

        <Button
          onClick={() => handleReset(clearFilters, confirm)}
          size="small"
          style={{ width: 90 }}
        >
          Zurücksetzen
        </Button>
      </div>
    ),

    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? COLORS.primary : undefined }} />
    ),

    onFilter: (value, record) => {
      const val = value.toLowerCase();

      if (dataIndex === "wohnortAdresse") {
        return (
          (record.wohnortAdresse &&
            record.wohnortAdresse.toLowerCase().includes(val)) ||
          (record.wohnort &&
            record.wohnort.name &&
            record.wohnort.name.toLowerCase().includes(val))
        );
      }

      if (dataIndex === "arbeitsortAdresse") {
        return (
          (record.arbeitsortAdresse &&
            record.arbeitsortAdresse.toLowerCase().includes(val)) ||
          (record.arbeitsort &&
            record.arbeitsort.name &&
            record.arbeitsort.name.toLowerCase().includes(val))
        );
      }

      return record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(val)
        : "";
    },

    onFilterDropdownVisibleChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },

    render: (text) =>
      searchedColumn === dataIndex ? (
        <span
          style={{
            backgroundColor: "#FFF7D6",
            color: COLORS.text,
            padding: "0 3px",
            borderRadius: 4,
          }}
        >
          {text}
        </span>
      ) : (
        text
      ),
  });

  const runBulkAddressCheck = async () => {
    if (!passengers?.length) {
      message.info("Keine Passagiere zum Prüfen vorhanden.");
      return;
    }

    const items = [];

    for (const p of passengers) {
      const wohn = p.wohnortAdresse || p.wohnort?.name || "";
      const work = p.arbeitsortAdresse || p.arbeitsort?.name || "";

      items.push({
        passengerId: p.id,
        name: p.name,
        field: "wohnortAdresse",
        address: wohn,
      });

      items.push({
        passengerId: p.id,
        name: p.name,
        field: "arbeitsortAdresse",
        address: work,
      });
    }

    setBulkChecking(true);
    setBulkProgress({ done: 0, total: items.length });
    setBulkResult({
      okCount: 0,
      failCount: 0,
      total: items.length,
      results: [],
    });

    try {
      const res = await axios.post(GEOCODE_BULK_ENDPOINT, { items });

      const data = res.data || {};
      const results = Array.isArray(data.results) ? data.results : [];

      setBulkResult({
        okCount: data.okCount ?? 0,
        failCount: data.failCount ?? results.filter((r) => !r.ok).length,
        total: data.total ?? items.length,
        results,
      });

      setBulkProgress({ done: items.length, total: items.length });

      const bad = new Set();
      for (const r of results) {
        if (!r.ok && r.passengerId != null) {
          bad.add(r.passengerId);
        }
      }

      setInvalidPassengerIds(bad);

      if ((data.failCount ?? 0) === 0) {
        message.success(`Alle Adressen ok (${data.okCount ?? items.length}).`);
      } else {
        message.warning(
          `Adressprüfung: ${data.okCount ?? 0} ok, ${
            data.failCount ?? 0
          } fehlerhaft.`
        );
      }

      setBulkResultOpen(true);
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Bulk-Prüfung fehlgeschlagen.";
      message.error(msg);
    } finally {
      setBulkChecking(false);
    }
  };

  const columns = [
    {
      title: "Aktiv",
      dataIndex: "aktiv",
      width: 140,
      align: "center",
      filters: [
        { text: "Aktiv", value: true },
        { text: "Inaktiv", value: false },
      ],
      onFilter: (value, record) => record.aktiv === value,
      sorter: (a, b) => (a.aktiv === b.aktiv ? 0 : a.aktiv ? -1 : 1),
      render: (_, passenger) => (
        <Switch
          checked={passenger.aktiv}
          checkedChildren="Aktiv"
          unCheckedChildren="Inaktiv"
          onChange={() => handleToggleActive(passenger)}
          style={{
            background: passenger.aktiv ? "#52c41a" : "#ff4d4f",
            whiteSpace: "nowrap",
          }}
        />
      ),
      fixed: "left",
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 160,
      sorter: (a, b) => comparePassengerNames(a.name, b.name),
      defaultSortOrder: "ascend",
      ...getColumnSearchProps("name"),
      fixed: "left",
    },
    {
      title: "Wohnort-Adresse",
      dataIndex: "wohnortAdresse",
      width: 220,
      sorter: (a, b) =>
        (a.wohnortAdresse || a.wohnort?.name || "").localeCompare(
          b.wohnortAdresse || b.wohnort?.name || ""
        ),
      render: (_, record) =>
        record.wohnortAdresse || record.wohnort?.name || "-",
      ...getColumnSearchProps("wohnortAdresse"),
    },
    {
      title: "Arbeitsort / Einrichtung",
      dataIndex: "arbeitsortAdresse",
      width: 220,
      sorter: (a, b) =>
        (a.arbeitsortAdresse || a.arbeitsort?.name || "").localeCompare(
          b.arbeitsortAdresse || b.arbeitsort?.name || ""
        ),
      render: (_, record) => {
        const address = record.arbeitsortAdresse || record.arbeitsort?.name || "-";
        return record.facilityName ? `${record.facilityName} – ${address}` : address;
      },
      ...getColumnSearchProps("arbeitsortAdresse"),
    },
    {
      title: "Transport",
      dataIndex: "taxi",
      width: 130,
      align: "center",
      filters: [
        { text: "Taxi", value: true },
        { text: "Bus", value: false },
      ],
      onFilter: (value, record) => record.taxi === value,
      sorter: (a, b) => (a.taxi === b.taxi ? 0 : a.taxi ? -1 : 1),
      render: (taxi) => (
        <Tag color={taxi ? "gold" : "cyan"}>{taxi ? "Taxi" : "Bus"}</Tag>
      ),
    },
    {
      title: "Rollstuhl",
      dataIndex: "rollstuhl",
      width: 145,
      align: "center",
      filters: [
        { text: "Ja", value: true },
        { text: "Nein", value: false },
      ],
      onFilter: (value, record) => record.rollstuhl === value,
      sorter: (a, b) =>
        a.rollstuhl === b.rollstuhl ? 0 : a.rollstuhl ? -1 : 1,
      render: (v) => (v ? "Ja" : "Nein"),
    },
    {
      title: "E-Rollstuhl",
      dataIndex: "eRollstuhl",
      width: 165,
      align: "center",
      filters: [
        { text: "Ja", value: true },
        { text: "Nein", value: false },
      ],
      onFilter: (value, record) => record.eRollstuhl === value,
      sorter: (a, b) =>
        a.eRollstuhl === b.eRollstuhl ? 0 : a.eRollstuhl ? -1 : 1,
      render: (v) => (v ? "Ja" : "Nein"),
    },
    {
      title: "Begleitperson",
      dataIndex: "beifahrer",
      width: 145,
      align: "center",
      filters: [
        { text: "Ja", value: true },
        { text: "Nein", value: false },
      ],
      onFilter: (value, record) => record.beifahrer === value,
      sorter: (a, b) =>
        a.beifahrer === b.beifahrer ? 0 : a.beifahrer ? -1 : 1,
      render: (v) => (v ? "Ja" : "Nein"),
    },
    {
      title: "Umsteigezeit",
      dataIndex: "umsteigezeitMinuten",
      width: 155,
      align: "center",
      sorter: (a, b) =>
        (a.umsteigezeitMinuten ??
          (a.einstiegszeitMinuten ?? 0) + (a.ausstiegszeitMinuten ?? 0)) -
        (b.umsteigezeitMinuten ??
          (b.einstiegszeitMinuten ?? 0) + (b.ausstiegszeitMinuten ?? 0)),
      render: (_, record) =>
        `${
          record.umsteigezeitMinuten ??
          (record.einstiegszeitMinuten ?? 0) + (record.ausstiegszeitMinuten ?? 0)
        } min`,
    },
    {
      title: "Nicht zusammen",
      dataIndex: "unvertraeglichePassengerIds",
      width: 210,
      filters: [
        { text: "Ausschlüsse hinterlegt", value: true },
        { text: "Keine Ausschlüsse", value: false },
      ],
      onFilter: (value, record) =>
        (Array.isArray(record.unvertraeglichePassengerIds) && record.unvertraeglichePassengerIds.length > 0) === value,
      render: (ids) => {
        const values = Array.isArray(ids) ? ids : [];
        if (!values.length) return "-";

        return values.map((id) => {
          const passenger = passengers.find((p) => p.id === id);
          return (
            <Tag key={id} color="volcano" style={{ marginBottom: 4 }}>
              {passenger?.name || `ID ${id}`}
            </Tag>
          );
        });
      },
    },
    {
      title: "Herausfordernd",
      dataIndex: "herausforderndesVerhalten",
      width: 170,
      align: "center",
      filters: [
        { text: "Ja", value: true },
        { text: "Nein", value: false },
      ],
      onFilter: (value, record) => record.herausforderndesVerhalten === value,
      sorter: (a, b) =>
        a.herausforderndesVerhalten === b.herausforderndesVerhalten
          ? 0
          : a.herausforderndesVerhalten
          ? -1
          : 1,
      render: (v, record) =>
        v ? "Ja" : "Nein",
    },
    {
      title: "Notizen",
      dataIndex: "notizen",
      width: 240,
      ...notesColumnFilter,
    },
    {
      title: "Hinfahrt",
      dataIndex: "hinfahrtVerfuegbarkeit",
      width: 200,
      render: renderSortedWeekdays,
    },
    {
      title: "Rückfahrt",
      dataIndex: "rueckfahrtVerfuegbarkeit",
      width: 200,
      render: renderSortedWeekdays,
    },
    {
      title: "Aktionen",
      key: "action",
      width: 220,
      align: "left",
      fixed: "right",
      render: (_, record) => (
        <Space size={6}>
          <Button size="small" onClick={() => openEdit(record)}>
            Bearbeiten
          </Button>

          <Button size="small" danger onClick={() => handleDelete(record.id)}>
            Löschen
          </Button>
        </Space>
      ),
    },
  ];

  const bulkFailures = (bulkResult.results || []).filter((r) => !r.ok);

  const bulkFailureColumns = [
    { title: "Name", dataIndex: "name", width: 160 },
    {
      title: "Feld",
      dataIndex: "field",
      width: 140,
      render: (v) =>
        v === "wohnortAdresse"
          ? "Wohnort"
          : v === "arbeitsortAdresse"
          ? "Arbeitsort"
          : v,
    },
    { title: "Adresse", dataIndex: "address", width: 320 },
    { title: "Fehler", dataIndex: "error", width: 260 },
  ];

  const progressPercent =
    bulkProgress.total > 0
      ? Math.round((bulkProgress.done / bulkProgress.total) * 100)
      : 0;

  return (
    <div
      className="passenger-page"
      style={{ maxWidth: "100%", overflowX: "auto" }}
    >
      <h2 className="app-page-title">Passagiere</h2>

      <Card
        size="small"
        style={{
          marginBottom: 16,
          borderColor: limitExceeded
            ? "#ffccc7"
            : limitReached
            ? "#ffe58f"
            : COLORS.border,
          background: limitExceeded
            ? "#fff2f0"
            : limitReached
            ? "#fffbe6"
            : COLORS.primaryLight,
        }}
      >
        <Space wrap size="middle">
          <strong style={{ color: COLORS.text }}>
            Angelegte Fahrgäste / Limit:
          </strong>

          <Tag
            color={limitExceeded ? "red" : limitReached ? "gold" : "cyan"}
            style={{ fontSize: 14 }}
          >
            {passengerCount} / {limitText}
          </Tag>

          {limitReached && (
            <span
              style={{
                color: limitExceeded ? "#cf1322" : "#ad6800",
                fontWeight: 600,
              }}
            >
              {limitExceeded ? "Limit überschritten" : "Limit erreicht"}
            </span>
          )}
        </Space>
      </Card>

      {limitReached && (
        <Alert
          type={limitExceeded ? "error" : "warning"}
          showIcon
          style={{ marginBottom: 16 }}
          message={limitExceeded ? "Fahrgast-Limit überschritten" : "Fahrgast-Limit erreicht"}
          description={
            limitExceeded
              ? `Es sind ${passengerCount} Fahrgäste angelegt, erlaubt sind ${passengerLimit}. Bitte löschen Sie Fahrgäste oder erhöhen Sie den Tarif.`
              : `Es sind ${passengerCount} von ${passengerLimit} möglichen Fahrgästen angelegt. Bitte löschen Sie einen Fahrgast oder erhöhen Sie den Tarif, um weitere Fahrgäste anzulegen.`
          }
        />
      )}

      <Space style={{ marginBottom: 16 }} wrap>
        <Button type="primary" onClick={openCreate} disabled={limitReached}>
          Neuer Passagier
        </Button>

        <Upload
          accept=".xlsx,.xls,.numbers"
          showUploadList={false}
          customRequest={handlePassengerImport}
          disabled={importing}
        >
          <Button icon={<UploadOutlined />} loading={importing}>
            Excel/Numbers importieren
          </Button>
        </Upload>

        <Button
          icon={<DownloadOutlined />}
          onClick={handlePassengerExport}
          loading={exporting}
          disabled={!passengers.length || loading || importing}
        >
          Excel herunterladen
        </Button>

        <Button
          onClick={runBulkAddressCheck}
          loading={bulkChecking}
          disabled={!passengers.length}
        >
          Alle Adressen prüfen
        </Button>

        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => setDeleteAllOpen(true)}
          disabled={
            !passengers.length ||
            loading ||
            importing ||
            bulkChecking ||
            deleteAllLoading
          }
        >
          Alle Passagiere löschen
        </Button>

        {bulkChecking && (
          <div style={{ minWidth: 260 }}>
            <Progress percent={progressPercent} />
          </div>
        )}

        {invalidPassengerIds.size > 0 && (
          <Tag color="red">
            {invalidPassengerIds.size} Passagiere mit Adressfehlern markiert
          </Tag>
        )}
      </Space>

      {importResult && (
        <Alert
          type={importResult.type}
          showIcon
          closable
          onClose={() => setImportResult(null)}
          style={{ marginBottom: 16 }}
          message={importResult.message}
          description={
            <div>
              {importResult.parsedCount != null &&
                importResult.freeSlots != null && (
                  <div>
                    Gefundene Passagiere: {importResult.parsedCount}
                    <br />
                    Freie Plätze im aktuellen Tarif: {importResult.freeSlots}
                  </div>
                )}

              {importResult.details?.length > 0 && (
                <ul style={{ paddingLeft: 18, marginTop: 8, marginBottom: 0 }}>
                  {importResult.details.slice(0, 8).map((detail, index) => (
                    <li key={`${detail}-${index}`}>{detail}</li>
                  ))}
                  {importResult.details.length > 8 && (
                    <li>
                      Weitere {importResult.details.length - 8} Hinweise
                      ausgeblendet.
                    </li>
                  )}
                </ul>
              )}
            </div>
          }
        />
      )}

      <Table
        dataSource={passengers}
        columns={columns}
        rowKey="id"
        loading={loading}
        bordered
        pagination={{
          defaultPageSize: 12,
          showSizeChanger: true,
          pageSizeOptions: ["10", "12", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} von ${total} Passagieren`,
        }}
        scroll={{ x: 2325, y: 500 }}
        rowClassName={(record) =>
          invalidPassengerIds.has(record.id)
            ? "passenger-row passenger-row-invalid"
            : "passenger-row"
        }
      />

      <Modal
        open={deleteAllOpen}
        title="Alle Passagiere löschen?"
        okText="Alle löschen"
        cancelText="Abbrechen"
        okButtonProps={{ danger: true }}
        confirmLoading={deleteAllLoading}
        onOk={handleDeleteAll}
        onCancel={() => setDeleteAllOpen(false)}
      >
        <p>Diese Aktion löscht alle Passagiere deines Kontos dauerhaft.</p>
      </Modal>

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={editingPassenger ? "Passagier bearbeiten" : "Neuer Passagier"}
        footer={null}
        destroyOnClose
        width={1050}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          scrollToFirstError
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Bitte Name eingeben." }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="wohnortAdresse"
            label="Wohnort-Adresse"
            rules={[
              { required: true, message: "Bitte Wohnort-Adresse eingeben." },
              { min: 5, message: "Wohnort-Adresse ist zu kurz." },
            ]}
          >
            <Input placeholder="z. B. Königstraße 1, 70173 Stuttgart" />
          </Form.Item>

          <Form.Item label="Arbeitsort / Einrichtung" style={{ marginBottom: 8 }}>
            <Space.Compact block>
              <Form.Item name="facilityId" noStyle>
                <Select
                  allowClear showSearch optionFilterProp="label"
                  options={facilityOptions}
                  placeholder="Bestehende Einrichtung auswählen"
                  onChange={handleFacilityChange}
                />
              </Form.Item>
              <Button onClick={() => {
                facilityForm.setFieldsValue({
                  name: "",
                  address: form.getFieldValue("arbeitsortAdresse") || "",
                });
                setFacilityModalOpen(true);
              }}>Neu</Button>
            </Space.Compact>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, next) => prev.facilityId !== next.facilityId}>
            {({ getFieldValue }) => <Form.Item
              name="arbeitsortAdresse"
              label={getFieldValue("facilityId") ? "Adresse der Einrichtung" : "Arbeitsort-Adresse"}
              rules={[
                { required: true, message: "Bitte Arbeitsort-Adresse eingeben." },
                { min: 5, message: "Arbeitsort-Adresse ist zu kurz." },
              ]}
            >
              <Input
                disabled={!!getFieldValue("facilityId")}
                onBlur={findFacilityForAddress}
                placeholder="z. B. Industriestraße 12, Stuttgart"
              />
            </Form.Item>}
          </Form.Item>

          <Form.Item name="rollstuhl" valuePropName="checked">
            <Checkbox>Rollstuhl (manuell)</Checkbox>
          </Form.Item>

          <Form.Item name="eRollstuhl" valuePropName="checked">
            <Checkbox>E-Rollstuhl</Checkbox>
          </Form.Item>

          <Form.Item name="beifahrer" valuePropName="checked">
            <Checkbox>Begleitperson notwendig</Checkbox>
          </Form.Item>

          <Form.Item name="taxi" valuePropName="checked">
            <Checkbox>
              Taxifahrt (nicht in einem hinterlegten Bus einplanen)
            </Checkbox>
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, next) => prev.taxi !== next.taxi}>
            {({ getFieldValue }) =>
              getFieldValue("taxi") ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Dieser Fahrgast wird aus der Busplanung ausgeschlossen und in den PDF-Taxiübersichten aufgeführt. Taxikosten werden nicht berechnet."
                />
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="unvertraeglichePassengerIds"
            label="Nicht zusammen im Bus mit"
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={passengerSelectOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              placeholder="Fahrgäste auswählen"
            />
          </Form.Item>

          <Form.Item name="herausforderndesVerhalten" valuePropName="checked">
            <Checkbox>Herausforderndes Verhalten</Checkbox>
          </Form.Item>

          <Form.Item name="aktiv" valuePropName="checked">
            <Checkbox>Aktiv</Checkbox>
          </Form.Item>

          <Form.Item name="notizen" label="Notizen">
            <Input.TextArea
              maxLength={1000}
              autoSize={{ minRows: 3, maxRows: 6 }}
              placeholder="z. B. braucht Sitzerhöhung"
            />
          </Form.Item>

          <Form.Item
            name="hinfahrtVerfuegbarkeit"
            label="Hinfahrt-Verfügbarkeit"
          >
            <Checkbox.Group
              options={WEEKDAY_ORDER.map((w) => ({
                label: WEEKDAY_DE[w],
                value: w,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="rueckfahrtVerfuegbarkeit"
            label="Rückfahrt-Verfügbarkeit"
          >
            <Checkbox.Group
              options={WEEKDAY_ORDER.map((w) => ({
                label: WEEKDAY_DE[w],
                value: w,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="maxRideMinutes"
            label="Maximale Fahrzeit (Min.)"
            extra="Leer = Standardwert aus den Planungsvorgaben. Ein individueller Wert hat Vorrang."
            rules={[{ type: "number", min: 1, message: "Bitte mindestens 1 Minute angeben." }]}
          >
            <InputNumber min={1} precision={0} style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="umsteigezeitMinuten"
            label="Umsteigezeit (Min.)"
            rules={[
              {
                type: "number",
                min: 0,
                message: "Bitte 0 oder mehr Minuten angeben.",
              },
            ]}
          >
            <InputNumber min={0} precision={0} style={{ width: "100%" }} />
          </Form.Item>

          <Collapse
            style={{ marginBottom: 16 }}
            items={[{
              key: "time-overrides",
              label: "Individuelle Zeitfenster (optional)",
              children: <>
                <Alert type="info" showIcon style={{ marginBottom: 12 }}
                  message="Nur ausgefüllte Fenster überschreiben die Zeiten der Einrichtung beziehungsweise die Standardzeiten." />
                <Table rowKey="weekday" size="small" pagination={false} columns={overrideColumns}
                  dataSource={timeOverrides} scroll={{ x: 950 }} />
              </>,
            }]}
          />

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Speichern
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={facilityModalOpen}
        title="Neue Einrichtung"
        footer={null}
        destroyOnClose
        onCancel={() => setFacilityModalOpen(false)}
      >
        <Form form={facilityForm} layout="vertical" onFinish={createFacilityFromPassenger}>
          <Form.Item name="name" label="Name der Einrichtung" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="address" label="Adresse" rules={[{ required: true, min: 5 }]}><Input /></Form.Item>
          <Button type="primary" htmlType="submit" block>Einrichtung anlegen</Button>
        </Form>
      </Modal>

      <Modal
        open={bulkResultOpen}
        onCancel={() => setBulkResultOpen(false)}
        title="Adressprüfung – Ergebnis"
        footer={[
          <Button key="close" onClick={() => setBulkResultOpen(false)}>
            Schließen
          </Button>,
        ]}
        width={980}
        destroyOnClose
      >
        <Alert
          type={bulkResult.failCount === 0 ? "success" : "warning"}
          showIcon
          message={
            bulkResult.failCount === 0
              ? `Alles ok – ${bulkResult.okCount} Prüfungen erfolgreich.`
              : `Es gibt fehlerhafte Adressen: ${bulkResult.okCount} ok, ${bulkResult.failCount} fehlerhaft.`
          }
          style={{ marginBottom: 12 }}
        />

        {bulkResult.failCount > 0 ? (
          <Table
            dataSource={bulkFailures}
            columns={bulkFailureColumns}
            rowKey={(r) => `${r.passengerId}-${r.field}-${r.address}`}
            pagination={{ pageSize: 8 }}
            bordered
            scroll={{ x: 900 }}
          />
        ) : (
          <div>Keine fehlerhaften Adressen gefunden.</div>
        )}
      </Modal>

      <style>{`
        .passenger-page h2 {
          margin-top: 0;
          margin-bottom: 18px;
          color: #1F2937;
          font-weight: 700;
        }

        .passenger-row td {
          white-space: normal !important;
          word-break: break-word;
          line-height: 1.4;
          vertical-align: top;
          padding-top: 12px !important;
          padding-bottom: 12px !important;
        }

        .ant-table-thead > tr > th {
          white-space: nowrap;
          background: #F4FBFC !important;
          border-bottom: 1px solid #DCE7F3 !important;
          color: #1F2937 !important;
          font-weight: 700 !important;
        }

        .passenger-row-invalid td {
          background: #fff1f0 !important;
        }

        .passenger-row-invalid:hover td {
          background: #ffccc7 !important;
        }
      `}</style>
    </div>
  );
};

export default PassengersPage;
